import { observer } from "mobx-react-lite";
import { twMerge } from "tailwind-merge";

interface TextRotateProps {
    texts: [string, string];
    className?: string;
    /** true면 부모의 group hover를 사용 (부모에 group 클래스 필요) */
    useParentHover?: boolean;
    /** hover 후 애니메이션 시작까지의 딜레이 (ms). 기본값: 0 */
    delay?: 0 | 75 | 100 | 150 | 200 | 300 | 500 | 700 | 1000;
}

const delayClass: Record<number, string> = {
    0: "delay-0",
    75: "delay-75",
    100: "delay-100",
    150: "delay-150",
    200: "delay-200",
    300: "delay-300",
    500: "delay-500",
    700: "delay-700",
    1000: "delay-1000",
};

/**
 * 두 텍스트를 hover 시 카운트다운처럼 전환하는 컴포넌트.
 *
 * - 기본 상태: 첫 번째 텍스트 표시
 * - hover 시: 첫 번째 텍스트가 위로 올라가고, 두 번째 텍스트가 나타남
 * - 두 번째 텍스트가 없으면 전환 효과 없이 첫 번째 텍스트만 표시
 *
 * @example
 * // 기본 사용
 * <TextRotate texts={["English Title", "한글 제목"]} />
 *
 * // 부모 hover 사용 + 딜레이
 * <div className="group">
 *   <TextRotate texts={["Edit", "수정"]} useParentHover delay={150} />
 * </div>
 */
const TextRotate = observer(({ texts, className, useParentHover, delay = 0 }: TextRotateProps) => {
    const [first, second] = texts;
    const hasSecond = !!second;

    // 두 번째 텍스트가 없으면 첫 번째만 표시
    if (!hasSecond) {
        return (
            <span className={twMerge("truncate", className)}>
                {first}
            </span>
        );
    }

    return (
        <span
            className={twMerge(
                "inline-grid overflow-hidden",
                "[&>span]:col-start-1 [&>span]:row-start-1",
                !useParentHover && "group cursor-pointer",
                className
            )}
        >
            <span
                className={twMerge(
                    "truncate transition-all duration-300 ease-out",
                    delayClass[delay],
                    "opacity-100 translate-y-0",
                    "group-hover:opacity-0 group-hover:-translate-y-full"
                )}
            >
                {first}
            </span>
            <span
                className={twMerge(
                    "truncate transition-all duration-300 ease-out",
                    delayClass[delay],
                    "opacity-0 translate-y-full",
                    "group-hover:opacity-100 group-hover:translate-y-0"
                )}
            >
                {second}
            </span>
        </span>
    );
});

export default TextRotate;